import React, { useState } from 'react';
import { Globe, Loader2, AlertCircle, ExternalLink, Sparkles } from 'lucide-react';

interface Source {
  url: string;
  title: string;
}

interface BriefingResponse {
  briefing: string;
  sources: Source[];
}

const API = '/api/cybint';

// Very light renderer: split on blank lines into paragraphs, turn "- " lines into
// bullets, and bold a leading "Label:" so the AI's section headings stand out.
const renderBriefing = (text: string) => {
  const blocks = text.split(/\n{2,}/);
  return blocks.map((block, i) => {
    const lines = block.split('\n');
    const isList = lines.every((l) => /^\s*[-*•]\s+/.test(l));
    if (isList) {
      return (
        <ul key={i} className="space-y-1.5 mb-3">
          {lines.map((l, j) => (
            <li key={j} className="flex items-start gap-2 text-sm text-gray-700 dark:text-slate-300">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-orange-500 flex-shrink-0" />
              <span>{l.replace(/^\s*[-*•]\s+/, '')}</span>
            </li>
          ))}
        </ul>
      );
    }
    const m = block.match(/^([A-Z][A-Za-z /&]{2,40}):\s*([\s\S]*)$/);
    if (m) {
      return (
        <p key={i} className="text-sm text-gray-700 dark:text-slate-300 mb-3">
          <span className="font-bold text-slate-900 dark:text-slate-100">{m[1]}: </span>
          {m[2]}
        </p>
      );
    }
    return (
      <p key={i} className="text-sm text-gray-700 dark:text-slate-300 mb-3 whitespace-pre-wrap">
        {block}
      </p>
    );
  });
};

const CybintLookup: React.FC = () => {
  const [target, setTarget] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BriefingResponse | null>(null);

  const run = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!target.trim()) return;
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const res = await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target: target.trim() }),
      });
      const raw = await res.text();
      let data: any;
      try {
        data = JSON.parse(raw);
      } catch {
        throw new Error(
          "The briefing service isn't responding. Locally, use `vercel dev` — the plain dev server " +
            "doesn't run the /api function."
        );
      }
      if (!res.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
      setResult(data as BriefingResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-4 rounded-lg border border-orange-200 dark:border-orange-500/30 bg-orange-50/60 dark:bg-orange-500/5 p-4">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-orange-500" />
        <h4 className="font-bold text-slate-900 dark:text-slate-100">AI CYBINT briefing — WHOIS, DNS &amp; breach exposure</h4>
      </div>
      <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">
        Claude searches WHOIS/DNS lookup pages and breach-notification coverage for a domain or email
        and writes a cited summary. Treat it as a lead, not proof — always open the sources and
        corroborate.
      </p>

      <form onSubmit={run} className="flex flex-col sm:flex-row gap-2 mb-3">
        <input
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          placeholder="Domain or email — e.g. example.com or name@example.com"
          className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
        <button
          type="submit"
          disabled={loading || !target.trim()}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 transition-colors"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
          Run check
        </button>
      </form>

      {loading && (
        <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">
          Searching WHOIS, DNS, and breach records — this can take 20–30 seconds.
        </p>
      )}

      {error && (
        <div className="flex items-start gap-2 text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-lg p-3 mb-3">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-4">
          {renderBriefing(result.briefing)}

          {result.sources.length > 0 && (
            <div className="mt-4 pt-3 border-t border-gray-100 dark:border-slate-700">
              <p className="text-xs font-bold tracking-wide uppercase text-orange-500 mb-2">
                Sources ({result.sources.length})
              </p>
              <ul className="space-y-1.5">
                {result.sources.map((s) => (
                  <li key={s.url}>
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-start gap-1 text-sm text-orange-600 dark:text-orange-400 hover:underline"
                    >
                      <ExternalLink className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                      <span className="break-all">{s.title}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CybintLookup;
